import React from 'react';
import cn from 'classnames';
import copy from 'copy-to-clipboard';
import { Selection } from 'prosemirror-state';

import { CopyOutline } from '@lyearn/icons';
import { localStorage, useReactNodeView } from '@lyearn/lygo-core';
import { PopupButton, SelectionMenu } from '@lyearn/molecules';
import { Box, Stack, Tooltip } from '@lyearn/ui';

import { LANGUAGES } from '../../CodeFence/refractor';
import { PERSISTENCE_KEY } from '../consts';
import { codeBlockHoverStyles, onHoverContainer, overrideButtonStyles } from './styles';

const CodeBlock: React.FC = () => {
  const { view, editableComponentRef, node } = useReactNodeView();
  const ref = React.useRef<HTMLPreElement>(null);
  const [isCodeCopied, setIsCodeCopied] = React.useState(false);
  const isEditable = !!view?.editable;

  const handleLanguageChange = (newLanguage: string) => {
    if (!view || !node || !ref.current) return;

    const { state, dispatch } = view;
    const { tr } = state;

    const { top, left } = ref.current.getBoundingClientRect();
    const result = view.posAtCoords({ top, left });

    if (result) {
      const transaction = tr
        .setSelection(Selection.near(view.state.doc.resolve(result.inside)))
        .setNodeMarkup(result.inside, undefined, {
          language: newLanguage,
        });

      localStorage.set(PERSISTENCE_KEY, newLanguage);
      dispatch(transaction);
    }
  };

  const handleCopy = () => {
    if (!node?.textContent) return;

    setIsCodeCopied(true);
    copy(node.textContent);
    setTimeout(() => {
      setIsCodeCopied(false);
    }, 500);
  };

  const label =
    Object.entries(LANGUAGES).find(([key, value]) => {
      return key === node?.attrs.language;
    })?.[1] ?? 'None';

  return (
    <Box className={cn('isolate flex w-full flex-1', onHoverContainer, codeBlockHoverStyles)}>
      <Stack className="absolute top-18 z-10 end-8" contentEditable="false" gapX={8}>
        {isEditable ? (
          <SelectionMenu
            className="w-fit"
            items={Object.entries(LANGUAGES).map(([key, value]) => ({
              label: value,
              id: key,
              value: key,
              type: 'item',
            }))}
            minWidth={18}
            overrides={{
              PopoverContent: {
                className: 'max-h-256',
              },
            }}
            selectedId={node?.attrs.language}
            onAction={(_, item) => {
              if (item.type === 'item') {
                handleLanguageChange(item.id);
              }
            }}>
            <PopupButton chevron className={cn('action-btns', overrideButtonStyles)}>
              {label}
            </PopupButton>
          </SelectionMenu>
        ) : null}
        <Tooltip open={isCodeCopied} title="Copied!">
          <PopupButton className="action-btns h-32 w-32 min-w-0" onClick={handleCopy}>
            <CopyOutline className="h-20 w-20 flex-shrink-0" fillColorToken="icon-secondary" />
          </PopupButton>
        </Tooltip>
      </Stack>
      <Box className="code-block w-full" data-language={node?.attrs.language}>
        <pre ref={ref} className="mt-12 rounded-4 p-24 text-text-paragraph">
          <code ref={editableComponentRef} spellCheck="false"></code>
        </pre>
      </Box>
    </Box>
  );
};

export default CodeBlock;
